/**
 * MissionSubKpiCard — consolidated per-subKPI card combining status display,
 * history table, and inline data entry into a single expandable surface.
 *
 * Replaces the separate subKPI card rendering + MissionHistoryViewer +
 * MissionDataEntry (MetricEntryGrid) on the Mission KPI tab.
 *
 * Design principles:
 *  - Each card is self-contained: header shows status, body shows history + add
 *  - Permission-gated: canEdit controls visibility of all mutation controls
 *  - Workflow-aware: data points carry a visual status (Live / Pending Approval)
 *  - Assignee-aware: header shows who's responsible for data entry
 */

import React, { useState, useCallback, useMemo } from "react";
import {
  createMissionMetricPoint,
  updateMissionMetricPoint,
  deleteMissionMetricPoint,
  fetchMissionMetricPointsByProject,
  type MissionMetricPointResponse,
} from "../../api/missionMetrics";
import { ApiError } from "../../api/client";
import { invalidateAndRefetch, QUERY_KEYS } from "../../api/queryCache";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./MissionSubKpiCard.module.css";
// DEV-816 / Skip: pencil-to-edit on the Performance Mission KPI card.
// Reuses the same Tray + MissionKpiWizard the configuration modal uses.
// Permission gate is canConfigure (passed by parent), which the parent
// computes from canAdminProject — only Project Lead / admins can edit.
import { Tray } from "../Tray/Tray";
import { MissionKpiWizard } from "../ProjectConfigModal/MissionKpiWizard";
import {
  getProjectKpiConfigOverride,
  upsertProjectKpiConfigOverride,
} from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import type { SubKpiInstance } from "../../Data/ProjectConfig/projectKpiConfigOverrides";

// ── Helpers ────────────────────────────────────────────────────

function invalidateMissionCache(): void {
  // Use invalidateAndRefetch — NOT a raw dispatchEvent. The cache
  // module requires the entry to be marked stale (fetchedAt = 0)
  // before subscribers can refetch; otherwise fetchForKey returns
  // the still-fresh cached value and the UI doesn't update.
  invalidateAndRefetch(QUERY_KEYS.mission);
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function titleize(key: string): string {
  return key
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatVal(v: number | null | undefined, decimals = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return Number.isInteger(v) ? String(v) : v.toFixed(decimals);
}

function formatDate(iso: string): string {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

type ValueType = "numeric" | "binary" | "percentage" | "milestone_set";

// ── SVG Icons (inline to avoid external deps) ─────────────────

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg className={styles.chevron} data-expanded={expanded}
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const UserIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

/**
 * Pencil icon for "edit KPI configuration" (DEV-816). Visible only when the
 * parent passes canConfigure=true. Hover-revealed at the card level so it
 * doesn't visually clutter the Performance view.
 */
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

// ── Types ──────────────────────────────────────────────────────

export interface MissionSubKpiCardProps {
  /** SubKPI result from the scoring engine */
  subKpi: any;
  /** Project GUID */
  projectGuid: string;
  /** All raw API points for this project */
  rawPoints: MissionMetricPointResponse[];
  /** Targets for this project, keyed by subKpiKey */
  projectTargets?: Record<string, any>;
  /** Whether this card starts expanded */
  defaultExpanded?: boolean;
  /** Whether user can enter data (canEditProjectContent — V1) */
  canEdit: boolean;
  /**
   * Whether user can edit KPI configuration: thresholds, color mode,
   * deadline, etc. (canAdminProject — V1; project:settings — V2). When
   * true, a pencil icon appears in the card header to invoke the
   * MissionKpiWizard tray. Defaults to false. (DEV-816)
   */
  canConfigure?: boolean;
  /** Display name of the assignee (resolved from assignee_guid) */
  assigneeName?: string;
  /** KPI indicator renderer from parent (for the status icon) */
  renderIndicator?: (sk: any) => React.ReactNode;
  /** Called after data changes so the host can refresh */
  onDataChanged?: () => void;
}

// ── Value Display ──────────────────────────────────────────────

function FormatValue({ value, valueType, unit, milestoneTotal, itemId, itemLabel }: {
  value: number; valueType: ValueType; unit?: string; milestoneTotal?: number;
  /** DEV-816 Option D: when set, this row is a per-item Checklist completion event. */
  itemId?: string | null;
  /** Resolved label for the item (looked up from the type's milestones array). */
  itemLabel?: string;
}) {
  switch (valueType) {
    case "binary":
      return value >= 1
        ? <span className={styles.binaryYes}>Yes</span>
        : <span className={styles.binaryNo}>No</span>;
    case "percentage":
      return <span>{Number.isFinite(value) ? value.toFixed(1) : "—"}%</span>;
    case "milestone_set":
      // Per-item row (Option D): render the item label with check mark.
      // The "1 of N" framing doesn't apply to per-item events — each row
      // is a single completion, not a count snapshot.
      if (itemId) {
        return (
          <span className={styles.milestoneBadge}>
            {value >= 1 ? '✓' : '○'} {itemLabel || itemId}
          </span>
        );
      }
      // Legacy count snapshot (no item_id): preserve old "N of M" display.
      return <span className={styles.milestoneBadge}>{Number.isFinite(value) ? value : "—"} of {milestoneTotal ?? "?"}</span>;
    default:
      return (
        <span>
          {formatVal(value)}
          {unit ? <span style={{ fontSize: "11px", opacity: 0.65, marginInlineStart: 3 }}>{unit}</span> : null}
        </span>
      );
  }
}

// ── Sparkline (DEV-816 / Skip) ────────────────────────────────
//
// Tiny inline trend chart rendered between the meta column and the score
// column. 64×24 SVG, color-matched to the indicator state. Returns null
// (no DOM, no flex space) when the series is too short to draw.

interface SparklineProps {
  series: number[];
  indicatorColor: string; // "0" | "1" | "2" | "3"
}
function Sparkline({ series, indicatorColor }: SparklineProps) {
  if (!series || series.length < 2) return null;
  const w = 64, h = 24, pad = 2;
  const min = Math.min(...series, 0);
  const max = Math.max(...series, 100);
  const range = max - min || 1;
  const stepX = (w - pad * 2) / (series.length - 1);
  const colorVar =
    indicatorColor === "1" ? "var(--color-error)" :
    indicatorColor === "2" ? "var(--color-warning)" :
    indicatorColor === "3" ? "var(--color-success)" :
                             "var(--accent-primary)";
  const points = series.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const lastX = pad + (series.length - 1) * stepX;
  const lastY = h - pad - ((series[series.length - 1] - min) / range) * (h - pad * 2);
  return (
    <svg
      className={styles.sparkline}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={colorVar}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      />
      <circle cx={lastX} cy={lastY} r="2" fill={colorVar} />
    </svg>
  );
}

// ── Editable Row ───────────────────────────────────────────────

function EditableRow({ point, valueType, unit, milestoneTotal, milestoneLabels, canEdit, onSave, onDelete }: {
  point: MissionMetricPointResponse;
  valueType: ValueType;
  unit?: string;
  milestoneTotal?: number;
  /** DEV-816 Option D: id → label map so per-item rows can render the
   *  human label instead of the raw item_id. */
  milestoneLabels?: Map<string, string>;
  canEdit: boolean;
  onSave: (id: string, value: number, note: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(point.value));
  const [editNote, setEditNote] = useState(point.note ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const n = Number(editValue);
    if (!Number.isFinite(n)) return;
    setSaving(true);
    try { await onSave(point.id, n, editNote); setEditing(false); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try { await onDelete(point.id); }
    finally { setSaving(false); }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") setEditing(false);
  };

  return (
    <tr>
      <td className={styles.dateCell}>{formatDate(point.reportingDate)}</td>
      <td className={styles.valueCell}>
        {editing ? (
          valueType === "binary" ? (
            <select value={editValue} onChange={(e) => setEditValue(e.target.value)}
              className={styles.editInput} style={{ width: 65, textAlign: "start" }}>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          ) : (
            <input type="number" className={styles.editInput} value={editValue}
              onChange={(e) => setEditValue(e.target.value)} onKeyDown={onKey}
              autoFocus step={valueType === "percentage" ? "0.1" : "1"} />
          )
        ) : (
          <FormatValue
            value={point.value}
            valueType={valueType}
            unit={unit}
            milestoneTotal={milestoneTotal}
            itemId={point.itemId ?? null}
            itemLabel={point.itemId ? milestoneLabels?.get(point.itemId) : undefined}
          />
        )}
      </td>
      <td className={styles.noteCell}>
        {editing ? (
          <input type="text" className={styles.editNoteInput} value={editNote}
            onChange={(e) => setEditNote(e.target.value)} onKeyDown={onKey} placeholder="Note" />
        ) : (
          point.note || "—"
        )}
      </td>
      <td className={styles.statusCell}>
        <span className={styles.statusLive}>Live</span>
      </td>
      {canEdit && (
        <td className={styles.actionsCell}>
          {editing ? (
            <>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? "…" : "Save"}
              </button>
              <button className={styles.actionBtn} onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className={styles.actionBtn} onClick={() => {
                setEditValue(String(point.value));
                setEditNote(point.note ?? "");
                setEditing(true);
              }}>Edit</button>
              <button className={styles.deleteBtn} onClick={handleDelete} disabled={saving}>
                {saving ? "…" : "Del"}
              </button>
            </>
          )}
        </td>
      )}
    </tr>
  );
}

// ── Main Component ─────────────────────────────────────────────

const INITIAL_VISIBLE = 6;

const MissionSubKpiCard: React.FC<MissionSubKpiCardProps> = ({
  subKpi: sk,
  projectGuid,
  rawPoints,
  projectTargets,
  defaultExpanded = false,
  canEdit,
  canConfigure = false,
  assigneeName,
  renderIndicator,
  onDataChanged,
}) => {
  const { t: tKpi } = useAppTranslation("kpi");

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showAll, setShowAll] = useState(false);
  const [status, setStatus] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  // DEV-816 / Skip: KPI configuration tray state. Open by clicking the pencil
  // in the card header. Tray hosts the same MissionKpiWizard the configuration
  // modal uses, prefilled with this KPI's current SubKpiInstance.
  const [configTrayOpen, setConfigTrayOpen] = useState(false);

  // Add-record form state
  const [addDate, setAddDate] = useState(todayISO());
  const [addValue, setAddValue] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  // DEV-816 Option D / Skip's mockup-driven decision (Option A): click
  // marks complete with today's date. No top-level date picker. The date
  // shown next to each checked item is clickable to edit if backdating
  // is needed. Tracks which item (if any) is currently being date-edited.
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemDate, setEditingItemDate] = useState<string>("");

  // Resolve subKPI properties
  const subKpiKey = String(sk?.subKpiKey ?? "");
  const displayName = sk?.label || titleize(subKpiKey);
  const valueType: ValueType = (() => {
    const vt = String(sk?.value_type ?? "numeric");
    if (vt === "binary" || vt === "percentage" || vt === "milestone_set") return vt;
    return "numeric";
  })();
  const latestValue = sk?.latest_value;
  const targetGoal = sk?.target_goal;
  const indicatorColor = String(sk?.indicator_color_value ?? "0");
  const score = (() => {
    const v = sk?.score_0_100;
    if (v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  })();
  const isTrackedOnly = (sk?.weight_configured ?? sk?.weight_used ?? 0) === 0;
  const skTags: string[] = Array.isArray(sk?.tags) ? sk.tags : [];
  const deadlineDate = sk?.deadline_date;
  const trackingStartDate = sk?.tracking_start_date as string | undefined;
  const unit = projectTargets?.[subKpiKey]?.unit ?? sk?.uom ?? undefined;
  const milestoneTotal = projectTargets?.[subKpiKey]?.milestone_total ?? sk?.milestone_total ?? undefined;

  // Tracking lifecycle: if start date is in the future, data entry is gated
  const trackingStarted = useMemo(() => {
    if (!trackingStartDate) return true; // no start date → always active
    return trackingStartDate <= todayISO();
  }, [trackingStartDate]);

  // Filter + sort points for this subKPI
  const points = useMemo(() => {
    const nk = subKpiKey.toUpperCase();
    return (rawPoints || [])
      .filter((p) => String(p.subKpiKey).toUpperCase() === nk)
      .sort((a, b) => b.reportingDate.localeCompare(a.reportingDate));
  }, [rawPoints, subKpiKey]);

  // DEV-816 Option D / Skip: id → label map for Checklist items so the
  // history table can render the human label instead of the raw item_id.
  // Built once per card render from the type config; falls back to empty
  // map for non-Checklist types.
  const milestoneLabels = useMemo(() => {
    const map = new Map<string, string>();
    if (valueType !== "milestone_set") return map;
    const cfg = (sk?.mission_type_config ?? {}) as { milestones?: Array<{ id: string; label: string }> };
    const items = Array.isArray(cfg.milestones) ? cfg.milestones : [];
    for (const m of items) {
      if (m.id) map.set(m.id, m.label || m.id);
    }
    return map;
  }, [sk, valueType]);

  const visiblePoints = showAll ? points : points.slice(0, INITIAL_VISIBLE);

  // ── Progress summary text ──────────────────────────────────
  const progressText = useMemo(() => {
    if (valueType === "binary") return null; // binary uses badge instead
    if (latestValue == null && targetGoal == null) return tKpi("mission.noDataYet") || "No data yet";
    const parts: string[] = [];
    if (latestValue != null) parts.push(formatVal(latestValue));
    if (targetGoal != null) parts.push(`of ${formatVal(targetGoal)}`);
    if (unit) parts.push(unit);
    return parts.join(" ");
  }, [valueType, latestValue, targetGoal, unit, tKpi]);

  // DEV-864: Bar fill = engine's `progress_0_100` (raw % toward target,
  // NO deadline penalty). Distinct from `score_0_100` which includes
  // deadline degradation — using score for the bar made the fill lag the
  // user's input proportionally inside warning/critical windows. (Skip's
  // report: with current=5, target=10, deadline 28d away, raw progress is
  // 50% but bar filled ~40% because score was capped to 75 by deadline
  // warning then conflated with progress for the bar render.)
  //
  // Color, grade, threshold ticks, and the headline score number all keep
  // using `score_0_100` — those are the deadline-aware quantities. Only
  // the bar fill switches to progress.
  //
  // Binary types use a badge instead of a bar, so progress is null and
  // the bar collapses to 0 width via the early return.
  //
  // Pre-DEV-864 history: DEV-816 had switched the bar from raw `latest /
  // target` to `score_0_100` to fix a Reduction direction bug. That was
  // right-direction but conflated progress with the deadline-aware score.
  // DEV-864 separates them in the engine return so each consumer reads
  // the right field.
  const progressPct = useMemo(() => {
    if (valueType === "binary") return 0;
    const raw = (sk as any)?.progress_0_100;
    if (raw == null) return 0;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
  }, [valueType, sk]);

  // DEV-816 / Skip: sparkline series. Build from the most recent N points
  // for this sub-KPI. We use score-equivalent values so the sparkline's
  // y-axis matches the bar fill (both are 0-100). For Checklist we
  // compute completed-count-as-percent on each historical point.
  const sparklineSeries = useMemo<number[]>(() => {
    if (valueType === "binary") return [];
    if (!Array.isArray(points) || points.length === 0) return [];
    // For per-item Checklist points, we'd need the milestones config; for
    // legacy/numeric points we use the value directly normalized. Take
    // last 6 points in chronological order (oldest → newest).
    const recent = points.slice(0, 6).reverse();
    if (valueType === "milestone_set") {
      const total = milestoneTotal ?? 0;
      if (total === 0) return [];
      return recent.map(p => Math.max(0, Math.min(100, (Number(p.value) / total) * 100)));
    }
    // Numeric / percentage: normalize to 0-100 using the target.
    if (targetGoal == null || targetGoal === 0) {
      // Reduction or no target: show raw values, normalize across the series.
      const vals = recent.map(p => Number(p.value));
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const range = max - min || 1;
      // For decrease-is-good, invert so trend reads "up = better." For
      // increase-is-good, leave as-is. Defensive: normalize regardless.
      return vals.map(v => ((v - min) / range) * 100);
    }
    return recent.map(p => Math.max(0, Math.min(100, (Number(p.value) / Number(targetGoal)) * 100)));
  }, [points, valueType, milestoneTotal, targetGoal]);

  // DEV-816 / Skip: threshold tick positions on the progress bar. Only
  // meaningful when color_mode='score' (default) — Deadline and Pace use
  // different scales for their thresholds. Reads the user's configured
  // green_threshold (yellow boundary, default 80) and yellow_threshold
  // (red boundary, default 60).
  const thresholdTicks = useMemo<{ yellow: number; red: number } | null>(() => {
    if (valueType === "binary") return null;
    const colorMode = (sk?.color_mode ?? 'score') as string;
    if (colorMode !== 'score') return null;
    const yellow = Number(sk?.green_threshold ?? 80);
    const red = Number(sk?.yellow_threshold ?? 60);
    if (!Number.isFinite(yellow) || !Number.isFinite(red)) return null;
    return { yellow, red };
  }, [valueType, sk]);

  // DEV-816 Skip Option A: full-state cue — end-cap pip when score is 100.
  const isFull = score != null && score >= 100;

  // ── Deadline proximity ─────────────────────────────────────
  const deadlineInfo = useMemo(() => {
    if (!deadlineDate) return null;
    // Check if goal is met (suppress deadline warning when achieved)
    const goalMet = valueType === "binary"
      ? (latestValue != null && latestValue >= 1)
      : (latestValue != null && targetGoal != null && targetGoal > 0 && latestValue >= targetGoal);
    if (goalMet) return null;
    const daysLeft = Math.ceil((new Date(deadlineDate).getTime() - Date.now()) / 86400000);
    const crit = sk?.critical_days ?? 7;
    const warn = sk?.warning_days ?? 30;
    if (daysLeft < 0) return { text: tKpi("mission.overdue") || "Overdue", cls: styles.deadlineOverdue };
    if (daysLeft <= crit) return { text: `${daysLeft}d left`, cls: styles.deadlineOverdue };
    if (daysLeft <= warn) return { text: `${daysLeft}d left`, cls: styles.deadlineWarning };
    return null;
  }, [deadlineDate, latestValue, targetGoal, valueType, sk, tKpi]);

  // ── Save handlers ──────────────────────────────────────────
  const handleEditSave = useCallback(async (id: string, value: number, note: string) => {
    setStatus(null);
    try {
      await updateMissionMetricPoint(id, { value, note: note || undefined });
      invalidateMissionCache();
      setStatus({ msg: tKpi("dataEntry.saveSuccess", { count: 1 }) || "Updated.", type: "success" });
      onDataChanged?.();
    } catch (err: any) {
      setStatus({ msg: `Update failed: ${err?.message ?? "unknown"}`, type: "error" });
    }
  }, [onDataChanged, tKpi]);

  const handleDelete = useCallback(async (id: string) => {
    setStatus(null);
    try {
      await deleteMissionMetricPoint(id);
      invalidateMissionCache();
      setStatus({ msg: "Deleted.", type: "success" });
      onDataChanged?.();
    } catch (err: any) {
      setStatus({ msg: `Delete failed: ${err?.message ?? "unknown"}`, type: "error" });
    }
  }, [onDataChanged]);

  const handleAdd = useCallback(async () => {
    if (!addDate || addValue.trim() === "") return;
    const numVal = Number(addValue);
    if (!Number.isFinite(numVal)) return;

    setAddSaving(true);
    setStatus(null);
    try {
      await createMissionMetricPoint({
        projectId: projectGuid,
        subKpiKey,
        reportingDate: addDate,
        value: numVal,
        note: addNote.trim() || undefined,
      });
      invalidateMissionCache();
      setAddValue("");
      setAddNote("");
      setStatus({ msg: tKpi("dataEntry.saveSuccess", { count: 1 }) || "Saved.", type: "success" });
      onDataChanged?.();
    } catch (err: any) {
      // 409 = duplicate — update instead
      if (err instanceof ApiError && err.status === 409) {
        try {
          const existing = await fetchMissionMetricPointsByProject(projectGuid, subKpiKey);
          const match = existing.find((p) => p.reportingDate === addDate);
          if (match) {
            await updateMissionMetricPoint(match.id, { value: numVal, note: addNote.trim() || undefined });
            invalidateMissionCache();
            setAddValue("");
            setAddNote("");
            setStatus({ msg: "Updated existing record.", type: "success" });
            onDataChanged?.();
          } else {
            setStatus({ msg: "Conflict but no matching record found.", type: "error" });
          }
        } catch (innerErr: any) {
          setStatus({ msg: `Update failed: ${innerErr?.message ?? "unknown"}`, type: "error" });
        }
      } else {
        setStatus({ msg: `Save failed: ${err?.message ?? "unknown"}`, type: "error" });
      }
    } finally {
      setAddSaving(false);
    }
  }, [projectGuid, subKpiKey, addDate, addValue, addNote, onDataChanged, tKpi]);

  const onAddKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleAdd();
  }, [handleAdd]);

  // DEV-816 Option D / Skip: Checklist item toggle handler.
  // Marking complete: POST a new metric point with item_id, value=1, and the
  // date currently in the `checklistMarkDate` picker (defaults to today,
  // user-overridable so historical completions can be recorded). Marking
  // incomplete: find and DELETE the existing item_id-bearing point. Update
  // value-only on previously-zero rows. After any change, onDataChanged
  // fires so the parent refetches.
  const handleToggleChecklistItem = useCallback(async (itemId: string, markComplete: boolean) => {
    setStatus(null);
    try {
      const existing = points.find((p) => (p as any).itemId === itemId) as MissionMetricPointResponse | undefined;
      if (markComplete) {
        if (existing && Number(existing.value) >= 1) return; // already complete, no-op
        if (existing) {
          await updateMissionMetricPoint(existing.id, { value: 1 });
        } else {
          await createMissionMetricPoint({
            projectId: projectGuid,
            subKpiKey,
            reportingDate: todayISO(),
            value: 1,
            itemId,
          });
        }
      } else {
        if (!existing) return;
        await deleteMissionMetricPoint(existing.id);
      }
      onDataChanged?.();
    } catch (err: any) {
      setStatus({ msg: `Toggle failed: ${err?.message ?? "unknown"}`, type: "error" });
    }
  }, [points, projectGuid, subKpiKey, onDataChanged]);

  // DEV-816 Option D / Skip: edit a previously-set met_date inline. Click
  // the date next to a checked item → opens an inline date input → blur or
  // Enter saves. The reporting_date column on the existing row is updated
  // via the standard PUT endpoint.
  const handleEditChecklistDate = useCallback(async (itemId: string, newDate: string) => {
    setStatus(null);
    setEditingItemId(null);
    setEditingItemDate("");
    if (!newDate) return;
    try {
      const existing = points.find((p) => (p as any).itemId === itemId) as MissionMetricPointResponse | undefined;
      if (!existing) return;
      if (existing.reportingDate === newDate) return; // no change
      // The current PUT endpoint accepts value + note only. To change the
      // reporting date we need to delete and re-create with the new date.
      // (If a future endpoint allows updating reportingDate, swap to it.)
      await deleteMissionMetricPoint(existing.id);
      await createMissionMetricPoint({
        projectId: projectGuid,
        subKpiKey,
        reportingDate: newDate,
        value: 1,
        itemId,
      });
      onDataChanged?.();
    } catch (err: any) {
      setStatus({ msg: `Date edit failed: ${err?.message ?? "unknown"}`, type: "error" });
    }
  }, [points, projectGuid, subKpiKey, onDataChanged]);

  // ── Configuration tray (DEV-816) ────────────────────────────
  // Look up the existing SubKpiInstance for this KPI from the project's
  // KPI config override. The wizard needs the FULL instance shape to
  // reverse-bridge into typeConfig on edit.
  const editingInstance = useMemo<SubKpiInstance | undefined>(() => {
    if (!configTrayOpen) return undefined;
    const ov = getProjectKpiConfigOverride(projectGuid as any);
    const list = (ov?.category_instances?.mission ?? []) as SubKpiInstance[];
    // The card's subKpiKey is whatever App.tsx used as the engine config
    // key — typically inst.outcome_id || inst.instance_guid || display_name.
    // Match case-insensitively because GUIDs are sometimes stored upper.
    const needle = subKpiKey.toUpperCase();
    return list.find((i) => {
      const k =
        i.outcome_id ||
        i.instance_guid ||
        i.display_name ||
        '';
      return String(k).toUpperCase() === needle;
    });
  }, [configTrayOpen, projectGuid, subKpiKey]);

  /**
   * Save handler for the in-card config tray. Mirrors what
   * ProjectConfigModal.commitMissionKpiFromWizard does, but operates
   * directly on the project KPI override store — no draft-state
   * round-trip through the modal. After save, the engine recomputes via
   * the existing event/cache invalidation, and the card itself re-renders
   * with the new config (next render of the parent).
   */
  const handleConfigSave = useCallback((updated: SubKpiInstance) => {
    const ov = getProjectKpiConfigOverride(projectGuid as any);
    const existingMission = (ov?.category_instances?.mission ?? []) as SubKpiInstance[];
    const idx = existingMission.findIndex((i) => {
      const oldGuid = String(i.instance_guid || '').toUpperCase();
      const newGuid = String(updated.instance_guid || '').toUpperCase();
      return oldGuid && oldGuid === newGuid;
    });
    let nextMission = [...existingMission];
    if (idx >= 0) {
      // Preserve weight + is_mandatory from the existing instance — the
      // wizard doesn't surface them and shouldn't accidentally clear them.
      const prior = existingMission[idx];
      nextMission[idx] = {
        ...updated,
        weight: updated.weight ?? prior.weight,
        is_mandatory: prior.is_mandatory,
      } as SubKpiInstance;
    } else {
      // Shouldn't normally happen — pencil only shows for existing KPIs.
      // But if it does, append rather than silently dropping the change.
      nextMission.push(updated);
    }
    upsertProjectKpiConfigOverride({
      project_guid: projectGuid as any,
      category_instances: {
        ...(ov?.category_instances ?? {}),
        mission: nextMission,
      } as any,
    } as any);
    setConfigTrayOpen(false);
    onDataChanged?.();
  }, [projectGuid, onDataChanged]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className={styles.card} data-expanded={expanded}>
      {/* ── Header ── */}
      <div className={styles.header} onClick={() => setExpanded((v) => !v)}>
        <div className={styles.colorBar} data-color={indicatorColor} />

        {renderIndicator && (
          <div className={styles.statusIcon}>{renderIndicator(sk)}</div>
        )}

        <div className={styles.headerContent}>
          <div className={styles.nameRow}>
            <span className={styles.name}>{displayName}</span>
            {isTrackedOnly && <span className={styles.tag}>{tKpi("mission.trackedOnly") || "Tracked-only"}</span>}
            {skTags.map((tag) => (
              <span key={tag} className={styles.tagCategory}>{tag}</span>
            ))}
            {!trackingStarted && trackingStartDate && (
              <span className={styles.tagUpcoming}>
                <ClockIcon /> {tKpi("mission.startsOn", { date: formatDate(trackingStartDate) }) || `Starts ${formatDate(trackingStartDate)}`}
              </span>
            )}
          </div>

          {/* Progress summary */}
          {valueType === "binary" ? (
            <div className={styles.binaryRow}>
              {latestValue != null && latestValue >= 1 ? (
                <span className={styles.badgeAchieved}><CheckIcon /> {tKpi("mission.achieved") || "Achieved"}</span>
              ) : latestValue != null ? (
                <span className={styles.badgeNotAchieved}>{tKpi("mission.notAchieved") || "Not Achieved"}</span>
              ) : (
                <span className={styles.badgeAwaiting}><ClockIcon /> {tKpi("mission.awaiting") || "Awaiting"}</span>
              )}
              {deadlineDate && (
                <span style={{ fontSize: 11, opacity: 0.7 }}>
                  {tKpi("mission.deadline") || "Deadline"}: {formatDate(deadlineDate)}
                </span>
              )}
              {deadlineInfo && <span className={deadlineInfo.cls}>{deadlineInfo.text}</span>}
            </div>
          ) : (
            <>
              <div className={styles.progressSummary}>
                {progressText && (
                  latestValue != null ? (
                    <>
                      <span className={styles.progressValue}>{formatVal(latestValue)}</span>
                      {targetGoal != null && (
                        <span className={styles.progressTarget}>of {formatVal(targetGoal)}{unit ? ` ${unit}` : ""}</span>
                      )}
                    </>
                  ) : (
                    <span style={{ opacity: 0.6, fontStyle: "italic" }}>{progressText}</span>
                  )
                )}
                {points.length > 0 && (
                  <>
                    <span className={styles.separator}>·</span>
                    <span style={{ fontSize: 11 }}>{points.length} {points.length === 1 ? "record" : "records"}</span>
                  </>
                )}
                {deadlineDate && (
                  <>
                    <span className={styles.separator}>·</span>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      {tKpi("mission.deadline") || "Deadline"}: {formatDate(deadlineDate)}
                    </span>
                  </>
                )}
                {deadlineInfo && <span className={deadlineInfo.cls}>{deadlineInfo.text}</span>}
              </div>
              {(latestValue != null || targetGoal != null) && (
                <div className={styles.progressBarOuter}>
                  <div
                    className={`${styles.progressBarInner} ${isFull ? styles.progressBarFull : ''}`}
                    data-color={indicatorColor}
                    style={{ width: `${progressPct}%` }}
                  />
                  {/* DEV-816 / Skip Option 5: threshold tick marks. Yellow
                      and red boundaries from the user's score-mode config.
                      Suppressed for Deadline / Pace modes (different scale). */}
                  {thresholdTicks && (
                    <>
                      <div
                        className={styles.progressTick}
                        data-band="yellow"
                        style={{ insetInlineStart: `${thresholdTicks.yellow}%` }}
                        title={`Yellow threshold: ${thresholdTicks.yellow}%`}
                      />
                      <div
                        className={styles.progressTick}
                        data-band="red"
                        style={{ insetInlineStart: `${thresholdTicks.red}%` }}
                        title={`Red threshold: ${thresholdTicks.red}%`}
                      />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* DEV-816 / Skip: sparkline of recent values, color-matched to the
            indicator state. Renders nothing when there's no series (binary
            type or single data point). Sits in the previously-empty space
            between the meta column and the score column. */}
        <Sparkline series={sparklineSeries} indicatorColor={indicatorColor} />

        {/* Score (DEV-816 / Skip's bug report: previously hidden for Binary,
            but Binary's achievement IS a score (100 when achieved, 70/80
            during deadline windows, 0 if overdue). Hiding it made achieved
            Binary KPIs look incomplete on the card. Now always rendered.
            The "Achieved" badge stays in addition — they convey different
            things: badge = state, score = numeric contribution to rollup.) */}
        <div className={styles.scoreCol}>
          <div className={styles.scoreNum}>{score === null ? "—" : Math.round(score)}</div>
          <div className={styles.scoreDen}>/100</div>
        </div>

        {/* DEV-816 / Skip: pencil-to-edit. Only shown when the parent
            granted canConfigure (admin-level permission). Stops click
            propagation so opening the tray doesn't also toggle expanded. */}
        {canConfigure && (
          <button
            type="button"
            className={styles.configBtn}
            aria-label="Edit KPI configuration"
            title="Edit KPI configuration"
            onClick={(e) => {
              e.stopPropagation();
              setConfigTrayOpen(true);
            }}
          >
            <PencilIcon />
          </button>
        )}

        <ChevronIcon expanded={expanded} />
      </div>

      {/* DEV-816: Configuration tray. Only mounts when open — the wizard's
          state resets between opens so an aborted edit doesn't leak into
          the next session. */}
      {configTrayOpen && editingInstance && (
        <Tray
          open={true}
          onClose={() => setConfigTrayOpen(false)}
          width={760}
        >
          <MissionKpiWizard
            mode="edit"
            existingInstance={editingInstance}
            projectGuid={projectGuid}
            onSave={handleConfigSave}
            onCancel={() => setConfigTrayOpen(false)}
          />
        </Tray>
      )}

      {/* ── Expanded body ── */}
      {expanded && (
        <div className={styles.body}>
          {/* Meta bar */}
          <div className={styles.metaBar}>
            <span className={styles.metaBadge}>
              {{ numeric: "Numeric", binary: "Gate", percentage: "Percentage", milestone_set: "Checklist" }[valueType]}
            </span>
            {assigneeName && (
              <span className={styles.metaAssignee}>
                <UserIcon /> {assigneeName}
              </span>
            )}
            {sk?.latest_date && (
              <span>{tKpi("dataEntry.colCurrent") || "Latest"}: {formatDate(sk.latest_date)}</span>
            )}
            {deadlineDate && (
              <span>{tKpi("mission.deadline") || "Deadline"}: {formatDate(deadlineDate)}</span>
            )}
          </div>

          {/* DEV-816 Option D / Skip: per-item Checklist state.
              Render each item's actual completion state from item_id-bearing
              data points (not first-N-in-order). Items with a point whose
              value >= 1 render as ✓ + completion date; others render as ◯.
              When canEdit, items are clickable: tap to toggle the completion
              state for today's date. Falls back to first-N rendering for
              legacy data points (no item_id). */}
          {valueType === "milestone_set" && (() => {
            const cfg = (sk?.mission_type_config ?? {}) as { milestones?: Array<{ id: string; label: string }> };
            const milestones = Array.isArray(cfg.milestones) ? cfg.milestones : [];
            if (milestones.length === 0) return null;

            // Build a map of item_id → latest completion point.
            const itemPoints = new Map<string, MissionMetricPointResponse>();
            let hasItemIdPoints = false;
            for (const p of points) {
              const itemId = (p as any).itemId as string | undefined;
              if (!itemId) continue;
              hasItemIdPoints = true;
              const prior = itemPoints.get(itemId);
              if (!prior || p.reportingDate > prior.reportingDate) {
                itemPoints.set(itemId, p);
              }
            }

            // Legacy fallback: no item_id rows → first-N-in-order rendering.
            const legacyCount = !hasItemIdPoints && Number.isFinite(latestValue)
              ? Math.max(0, Math.min(milestones.length, Math.round(latestValue as number)))
              : 0;

            const isItemDone = (m: { id: string }, idx: number): boolean => {
              if (hasItemIdPoints) {
                const p = itemPoints.get(m.id);
                return !!p && Number(p.value) >= 1;
              }
              return idx < legacyCount;
            };

            const completeCount = milestones.filter((m, i) => isItemDone(m, i)).length;

            return (
              <div className={styles.milestoneList} role="list" aria-label="Checklist items">
                {/* DEV-816 Option A (Skip's mockup-driven decision):
                    no top-level date picker. Click an item to mark complete
                    with today's date. The displayed met_date next to a
                    checked item is clickable to edit for backdating. */}
                {milestones.map((m, i) => {
                  const done = isItemDone(m, i);
                  const completionPoint = hasItemIdPoints ? itemPoints.get(m.id) : null;
                  const isEditingThis = editingItemId === m.id;
                  return (
                    <div
                      key={m.id || i}
                      className={`${styles.milestoneItem} ${done ? styles.milestoneItemDone : ''}`}
                      role="listitem"
                    >
                      {/* DEV-816 / Skip: ONLY the checkbox toggles the item.
                          Clicking the label or whitespace does nothing —
                          prevents accidental checks/unchecks during scrolling
                          or reading. The button is sized 28×28 with a clear
                          hover/focus state so it's an obvious target. */}
                      {canEdit ? (
                        <button
                          type="button"
                          className={`${styles.milestoneCheckButton} ${done ? styles.milestoneCheckButtonDone : ''}`}
                          onClick={() => handleToggleChecklistItem(m.id, !done)}
                          aria-label={done
                            ? `Mark "${m.label}" incomplete`
                            : `Mark "${m.label}" complete`}
                          aria-pressed={done}
                          title={done ? 'Click to mark incomplete' : 'Click to mark complete'}
                        >
                          <span aria-hidden="true">{done ? '✓' : ''}</span>
                        </button>
                      ) : (
                        <span className={styles.milestoneCheck} aria-hidden="true">{done ? '✓' : '◯'}</span>
                      )}
                      <span className={styles.milestoneLabel}>{m.label || `(item ${i + 1})`}</span>
                      {done && completionPoint && !isEditingThis && (
                        <span
                          className={`${styles.milestoneDate} ${canEdit ? styles.milestoneDateEditable : ''}`}
                          onClick={canEdit ? () => {
                            setEditingItemId(m.id);
                            setEditingItemDate(completionPoint.reportingDate);
                          } : undefined}
                          title={canEdit ? 'Click to edit completion date' : undefined}
                        >
                          {formatDate(completionPoint.reportingDate)}
                        </span>
                      )}
                      {done && isEditingThis && (
                        <span className={styles.milestoneDateEdit}>
                          <input
                            type="date"
                            value={editingItemDate}
                            autoFocus
                            onChange={(e) => setEditingItemDate(e.target.value)}
                            onBlur={() => handleEditChecklistDate(m.id, editingItemDate)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleEditChecklistDate(m.id, editingItemDate);
                              } else if (e.key === 'Escape') {
                                setEditingItemId(null);
                                setEditingItemDate("");
                              }
                            }}
                          />
                        </span>
                      )}
                    </div>
                  );
                })}
                <div className={styles.milestoneFoot}>
                  {completeCount} of {milestones.length} complete
                  {!hasItemIdPoints && completeCount > 0 && (
                    <span className={styles.milestoneLegacy}> · legacy data — click items to upgrade</span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* History table */}
          {points.length === 0 ? (
            <div className={styles.emptyHistory}>
              {tKpi("mission.noRecordsYet") || "No records yet."}
              {canEdit ? ` ${tKpi("mission.addFirstRecord") || "Add your first record below."}` : ""}
            </div>
          ) : (
            <>
              <table className={styles.historyTable}>
                <thead>
                  <tr>
                    <th>{tKpi("dataEntry.reportingDate") || "Date"}</th>
                    <th>{tKpi("dataEntry.colNewValue") || "Value"}</th>
                    <th>{tKpi("dataEntry.colNote") || "Note"}</th>
                    <th>{tKpi("mission.status") || "Status"}</th>
                    {canEdit && <th style={{ textAlign: "end" }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {visiblePoints.map((pt, idx) => (
                    <EditableRow
                      key={pt.id}
                      point={pt}
                      valueType={valueType}
                      unit={unit}
                      milestoneTotal={milestoneTotal}
                      milestoneLabels={milestoneLabels}
                      canEdit={canEdit}
                      onSave={handleEditSave}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
              {points.length > INITIAL_VISIBLE && (
                <button className={styles.showMoreBtn} onClick={() => setShowAll((v) => !v)}>
                  {showAll
                    ? (tKpi("mission.showLess") || "Show less")
                    : `${tKpi("mission.showAll") || "Show all"} ${points.length} records`}
                </button>
              )}
            </>
          )}

          {/* Not-yet-started notice */}
          {!trackingStarted && trackingStartDate && (
            <div className={styles.emptyHistory} style={{ fontStyle: "italic" }}>
              {tKpi("mission.trackingNotStarted", { date: formatDate(trackingStartDate) }) || `Data entry begins ${formatDate(trackingStartDate)}.`}
            </div>
          )}

          {/* Add record row — only for editors, gated by tracking start date.
              Hidden for Checklist (milestone_set) when item-level data entry
              is active: the milestone list above IS the data entry mechanism
              (click an item to toggle complete). The number-input row would
              be a redundant + confusing parallel path. (DEV-816 Option D) */}
          {canEdit && trackingStarted && valueType !== "milestone_set" && (
            <div className={styles.addRow}>
              <span className={styles.addLabel}>{tKpi("mission.addRecord") || "Add"}</span>
              <input
                type="date"
                className={styles.addDateInput}
                value={addDate}
                onChange={(e) => setAddDate(e.target.value)}
              />
              {valueType === "binary" ? (
                <select
                  className={styles.addSelect}
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                >
                  <option value="">—</option>
                  <option value="1">{tKpi("dataEntry.binaryYes") || "Yes"}</option>
                  <option value="0">{tKpi("dataEntry.binaryNo") || "No"}</option>
                </select>
              ) : (
                <input
                  type="number"
                  className={styles.addValueInput}
                  placeholder={tKpi("dataEntry.colNewValue") || "Value"}
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                  onKeyDown={onAddKey}
                  step={valueType === "percentage" ? "0.1" : "1"}
                />
              )}
              <input
                type="text"
                className={styles.addNoteInput}
                placeholder={tKpi("dataEntry.notePlaceholder") || "Note (optional)"}
                value={addNote}
                onChange={(e) => setAddNote(e.target.value)}
                onKeyDown={onAddKey}
              />
              <button
                className={styles.addSaveBtn}
                onClick={handleAdd}
                disabled={addSaving || addValue.trim() === ""}
              >
                {addSaving ? (tKpi("dataEntry.saving") || "Saving…") : (tKpi("common:save") || "Save")}
              </button>
            </div>
          )}

          {/* Status message */}
          {status && (
            <div className={status.type === "success" ? styles.statusSuccess : styles.statusError}>
              {status.msg}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MissionSubKpiCard;
